/**
 * ExceptionalFire — Canvas Fire Particle System
 * Overlay on hero fireplace image with mouse-driven intensity.
 * Vanilla JS, no dependencies. Uses requestAnimationFrame + IntersectionObserver.
 */
(function () {
  'use strict';

  var hero = document.getElementById('ef-hero-fire');
  var canvas = document.getElementById('ef-fire-canvas');
  if (!hero || !canvas) return;

  var ctx = canvas.getContext('2d');
  var particles = [];
  var intensity = 1;
  var isRunning = false;
  var animId = null;
  var isMobile = window.innerWidth < 768;
  var MAX_P = isMobile ? 35 : 70;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reducedMotion) {
    MAX_P = 10;
    intensity = 0.4;
  }

  /* ------------------------------------------------------------------ */
  /*  Fire zone — relative coordinates (fraction of canvas w/h)         */
  /*  Centered on the fireplace opening in a typical fireplace photo.   */
  /*  rx/ry = center point, rw/rh = spawn spread                       */
  /* ------------------------------------------------------------------ */
  /* Fire position in the ORIGINAL video (fraction of video's native dimensions) */
  /* 3rd glass panel from left = ~65% x, fire base = ~72% y */
  var fireInVideo = { x: 0.67, y: 0.68, w: 0.10, h: 0.05 };

  /* Computed fire zone in canvas pixels — updated on resize */
  var fireZone = { cx: 0, cy: 0, hw: 0, hh: 0 };

  /* ------------------------------------------------------------------ */
  /*  Canvas sizing + fire zone recalc for object-fit:cover              */
  /* ------------------------------------------------------------------ */
  function resize() {
    var rect = hero.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    /* Calculate how object-fit:cover maps video to container */
    var video = hero.querySelector('video');
    var vw = video ? video.videoWidth || 1920 : 1920;
    var vh = video ? video.videoHeight || 1080 : 1080;
    var containerRatio = rect.width / rect.height;
    var videoRatio = vw / vh;

    var renderW, renderH, offsetX, offsetY;
    if (containerRatio > videoRatio) {
      /* Container is wider — video scaled to width, cropped top/bottom */
      renderW = rect.width;
      renderH = rect.width / videoRatio;
      offsetX = 0;
      offsetY = (rect.height - renderH) / 2;
    } else {
      /* Container is taller — video scaled to height, cropped left/right */
      renderH = rect.height;
      renderW = rect.height * videoRatio;
      offsetX = (rect.width - renderW) / 2;
      offsetY = 0;
    }

    /* Map video-relative fire position to canvas pixels */
    fireZone.cx = offsetX + renderW * fireInVideo.x;
    fireZone.cy = offsetY + renderH * fireInVideo.y;
    fireZone.hw = renderW * fireInVideo.w * 0.5;
    fireZone.hh = renderH * fireInVideo.h * 0.5;
  }

  /* ------------------------------------------------------------------ */
  /*  Particle                                                          */
  /* ------------------------------------------------------------------ */
  function Particle() {
    this.reset();
  }

  Particle.prototype.reset = function () {
    var cx = fireZone.cx + (Math.random() - 0.5) * fireZone.hw * 2;
    var cy = fireZone.cy + (Math.random() - 0.5) * fireZone.hh * 2;
    this.x = cx;
    this.y = cy;
    this.vx = (Math.random() - 0.5) * 1;
    this.vy = -(Math.random() * 2.2 + 0.8);
    this.life = 1;
    this.decay = Math.random() * 0.014 + 0.007;
    this.size = Math.random() * 3 + 1.2;
    this.wind = (Math.random() - 0.5) * 0.3;
  };

  Particle.prototype.update = function () {
    this.vx += this.wind * 0.01;
    this.vy *= 0.995;
    this.x += this.vx * intensity;
    this.y += this.vy * intensity;
    this.life -= this.decay * intensity;
    this.size *= 0.992;
    if (this.life <= 0) this.reset();
  };

  Particle.prototype.draw = function () {
    var alpha = this.life * 0.85;
    var r, g, b;

    if (this.life > 0.8) {
      /* Base — subtle blue-white hint (not too blue) */
      r = 180;
      g = 170 + this.life * 40;
      b = 220 + this.life * 30;
    } else if (this.life > 0.55) {
      /* Rising — orange */
      r = 255;
      g = 100 + (this.life - 0.55) * 300;
      b = 15;
    } else if (this.life > 0.25) {
      /* Mid — bright orange-yellow */
      r = 255;
      g = 160 + (this.life - 0.25) * 250;
      b = 10;
    } else {
      /* Tips — yellow, fading */
      r = 255;
      g = 220 + this.life * 140;
      b = 40 + this.life * 200;
    }

    /* Glow halo behind each particle */
    if (this.size > 1.2 && this.life > 0.25) {
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size * 3, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(' + Math.floor(r) + ',' + Math.floor(g) + ',' + Math.floor(b) + ',' + (alpha * 0.1).toFixed(3) + ')';
      ctx.fill();
    }

    /* Draw flame-shaped particle (tall ellipse, not circle) */
    var w = Math.max(this.size * 0.7, 0.4);
    var h = Math.max(this.size * 1.8, 1);
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.vx * 0.3); /* lean with wind */
    ctx.scale(1, 1);
    ctx.beginPath();
    ctx.ellipse(0, 0, w, h, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(' + Math.floor(r) + ',' + Math.floor(g) + ',' + Math.floor(b) + ',' + alpha.toFixed(3) + ')';
    ctx.fill();
    ctx.restore();
  };

  /* ------------------------------------------------------------------ */
  /*  Initialise particles (staggered life so they don't all spawn at   */
  /*  the same time)                                                    */
  /* ------------------------------------------------------------------ */
  function init() {
    resize();
    for (var i = 0; i < MAX_P; i++) {
      var p = new Particle();
      p.life = Math.random(); // stagger
      particles.push(p);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Animation loop                                                    */
  /* ------------------------------------------------------------------ */
  function loop() {
    if (!isRunning) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (var i = 0; i < particles.length; i++) {
      particles[i].update();
      particles[i].draw();
    }

    animId = requestAnimationFrame(loop);
  }

  function start() {
    if (isRunning) return;
    isRunning = true;
    resize();
    loop();
  }

  function stop() {
    isRunning = false;
    if (animId) {
      cancelAnimationFrame(animId);
      animId = null;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Mouse interaction — cursor Y controls fire intensity              */
  /*  Top of hero = very slow (0.25x), bottom = very intense (1.75x)    */
  /*  Smooth-lerped target so flicker doesn't snap on quick mouse moves. */
  /* ------------------------------------------------------------------ */
  var intensityTarget = 1;
  if (!isMobile && !reducedMotion) {
    hero.addEventListener('mousemove', function (e) {
      var rect = hero.getBoundingClientRect();
      var normY = (e.clientY - rect.top) / rect.height;
      normY = Math.max(0, Math.min(1, normY));
      intensityTarget = 0.25 + normY * 1.5;
    });

    hero.addEventListener('mouseleave', function () {
      intensityTarget = 1;
    });

    /* Lerp intensity toward target on rAF — running outside the particle
       loop so it stays responsive even when fire is paused off-screen. */
    (function intensityLoop() {
      intensity += (intensityTarget - intensity) * 0.08;
      requestAnimationFrame(intensityLoop);
    })();
  }

  /* ------------------------------------------------------------------ */
  /*  Mobile: device orientation for intensity (subtle)                 */
  /* ------------------------------------------------------------------ */
  if (isMobile && !reducedMotion && window.DeviceOrientationEvent) {
    window.addEventListener('deviceorientation', function (e) {
      if (e.beta !== null) {
        // beta: -180..180, phone upright ~90
        var tilt = Math.max(0, Math.min(180, e.beta));
        var norm = tilt / 180; // 0 = flat, 1 = upside-down
        intensity = 0.6 + norm * 0.8;
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Finish selector — two-layer crossfade for frame swaps             */
  /*                                                                    */
  /*  Single <img> swaps replace the bitmap instantly — there's no way  */
  /*  to CSS-transition between two raster images on one element.       */
  /*  We clone the original overlay so there are two stacked layers     */
  /*  ("a" and "b"). On click we paint the new PNG onto the hidden      */
  /*  layer, wait for decode, then flip opacity on both — producing a   */
  /*  real crossfade with no flash of the base video underneath.        */
  /* ------------------------------------------------------------------ */
  var dots = hero.querySelectorAll('.ef-hero-fire__dot');
  var frameOverlayA = document.getElementById('ef-frame-overlay');
  var frameOverlayB = null;

  if (frameOverlayA && frameOverlayA.parentNode) {
    frameOverlayB = frameOverlayA.cloneNode(false);
    frameOverlayB.id = 'ef-frame-overlay-b';
    frameOverlayB.removeAttribute('src');
    frameOverlayB.style.opacity = '0';
    frameOverlayA.parentNode.insertBefore(frameOverlayB, frameOverlayA.nextSibling);
  }

  /* `activeLayer` holds the currently-visible img. `nextLayer` is the
     layer we paint onto for the next swap. Swapping them after a
     crossfade keeps state consistent. */
  var activeLayer = frameOverlayA;
  var nextLayer = frameOverlayB;

  /* Preload all frame PNGs so the hidden layer can decode instantly. */
  var framePreloads = {};
  if (dots.length) {
    for (var p = 0; p < dots.length; p++) {
      var pSrc = dots[p].getAttribute('data-frame');
      if (pSrc && pSrc !== 'none' && !framePreloads[pSrc]) {
        var pImg = new Image();
        pImg.decoding = 'async';
        pImg.src = pSrc;
        framePreloads[pSrc] = pImg;
      }
    }
  }

  if (dots.length && frameOverlayA) {
    for (var d = 0; d < dots.length; d++) {
      dots[d].addEventListener('click', handleFinishClick);
    }
  }

  function crossfadeTo(src) {
    if (!nextLayer || !activeLayer) return;

    var commit = function() {
      nextLayer.style.opacity = '1';
      activeLayer.style.opacity = '0';
      // Swap roles so the next click paints onto the (now hidden) other layer
      var tmp = activeLayer;
      activeLayer = nextLayer;
      nextLayer = tmp;
    };

    nextLayer.src = src;
    if (nextLayer.decode) {
      nextLayer.decode().then(commit).catch(commit);
    } else if (nextLayer.complete && nextLayer.naturalWidth > 0) {
      commit();
    } else {
      nextLayer.onload = commit;
      nextLayer.onerror = commit;
    }
  }

  function handleFinishClick() {
    var newSrc = this.getAttribute('data-frame');
    if (!frameOverlayA) return;

    // Update active dot immediately for responsive feel
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.remove('ef-hero-fire__dot--active');
    }
    this.classList.add('ef-hero-fire__dot--active');

    if (newSrc === 'none') {
      // Bronze = native video — hide both overlays to reveal it
      if (frameOverlayA) frameOverlayA.style.opacity = '0';
      if (frameOverlayB) frameOverlayB.style.opacity = '0';
      return;
    }

    // If current visible src already matches, nothing to do
    if (activeLayer && activeLayer.getAttribute('src') === newSrc && activeLayer.style.opacity === '1') {
      return;
    }

    crossfadeTo(newSrc);
  }

  /* ------------------------------------------------------------------ */
  /*  IntersectionObserver — pause when out of viewport                 */
  /* ------------------------------------------------------------------ */
  if ('IntersectionObserver' in window) {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          start();
        } else {
          stop();
        }
      });
    }, { threshold: 0.1 });

    observer.observe(hero);
  } else {
    /* Fallback: just run */
    start();
  }

  /* ------------------------------------------------------------------ */
  /*  Resize handler (debounced)                                        */
  /* ------------------------------------------------------------------ */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      isMobile = window.innerWidth < 768;
      if (isRunning) resize();
    }, 150);
  });

  /* ------------------------------------------------------------------ */
  /*  Bootstrap                                                         */
  /* ------------------------------------------------------------------ */
  init();
})();
